import { useEffect, useRef, useState } from "react";
import { csrfFetch } from "@lib/csrfFetch";
import { useUploadQueueStore } from "~/stores/uploadQueueStore";
import type { UploadRecord } from "~/lib/uploads";

// How often to refetch the upload payload while it's still transcoding. The
// ladder settles rungs over tens of seconds to minutes, so ~10s is frequent
// enough for the player to visibly upgrade (preview → original → rungs → ABR)
// without hammering the endpoint.
const POLL_INTERVAL_MS = 10_000;

export interface UploadProcessingPoll {
  // The freshest payload seen — the seed until the first successful refetch,
  // then whatever the endpoint last returned.
  upload: UploadRecord | null;
  // True while a poll cycle is active (processing_status === "processing").
  polling: boolean;
}

// Poll one upload's JSON while it's processing and hand back the fresh payload
// so a consumer (the player, the harness) upgrades live as the ladder lands.
// Polling stops the moment processing_status leaves "processing" (ready/failed)
// or the id is null. Seed with the payload you already have (e.g. the slot's
// `upload`) so the player renders immediately, before the first refetch.
export function useUploadProcessingPoll(
  uploadId: number | null,
  seed: UploadRecord | null = null
): UploadProcessingPoll {
  const [upload, setUpload] = useState<UploadRecord | null>(seed);
  // Track whether we're actively polling so the return value is honest and the
  // effect can be re-entrant-safe.
  const [polling, setPolling] = useState(false);
  // Terminal "the upload is gone" flag. A 404 mid-poll means the upload was
  // hard-deleted; the hook must stop itself rather than rely on the consumer
  // unmounting, or a still-mounted consumer 404s every interval forever.
  const [deleted, setDeleted] = useState(false);
  const cancelledRef = useRef(false);

  // Re-seed if the caller hands us a different seed (e.g. a slot flips to a
  // fresh Upload row) — but never clobber a newer polled payload with an older
  // seed for the same id.
  const seedId = seed?.id ?? null;
  useEffect(() => {
    if (seed && seed.id !== upload?.id) setUpload(seed);
  }, [seedId]);

  // A fresh upload id clears the terminal `deleted` latch — a hook re-pointed
  // at a new upload must poll again, not stay stopped from a previous 404.
  useEffect(() => {
    setDeleted(false);
  }, [uploadId]);

  const status = upload?.processing_status;
  const shouldPoll = uploadId != null && status === "processing" && !deleted;

  useEffect(() => {
    if (!shouldPoll || uploadId == null) {
      setPolling(false);
      return;
    }

    cancelledRef.current = false;
    setPolling(true);

    const tick = async () => {
      try {
        const res = await csrfFetch(`/api/v1/uploads/${uploadId}`, { method: "GET" });
        if (cancelledRef.current) return;
        // 404 mid-poll means the upload was hard-deleted (X-delete here or in
        // another tab). Broadcast it so any latched slot for this upload
        // renders null, and flip our terminal `deleted` flag so this hook stops
        // polling itself — a still-mounted consumer must not 404 every interval
        // forever.
        if (res.status === 404) {
          useUploadQueueStore.getState().markUploadDeleted(uploadId);
          if (!cancelledRef.current) setDeleted(true);
          return;
        }
        if (!res.ok) return;
        const fresh = (await res.json()) as UploadRecord;
        if (!cancelledRef.current) setUpload(fresh);
      } catch {
        // A transient failure just means we retry on the next interval.
      }
    };

    const handle = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      cancelledRef.current = true;
      clearInterval(handle);
    };
  }, [shouldPoll, uploadId]);

  return { upload, polling };
}
